import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import {
  surveyItemTypes,
  surveyModuleType,
  SurveyQuestion,
  SurveySection,
  SurveyTemplateResponse,
  xSurvey,
} from '../../_models/xsurvey';

@Injectable()
export class xSurveyUtilitiesService {
  survey$: BehaviorSubject<xSurvey> = new BehaviorSubject<xSurvey>(null);
  surveyTemplate$: BehaviorSubject<SurveyTemplateResponse> =
    new BehaviorSubject<SurveyTemplateResponse>(null);

  // form that gets written and where the answers are saved and read during the edit process
  surveyForm: SurveySection[] = [];
  // Template that is downloaded and must be left untouched (used for reading purposes)
  surveyTemplate: SurveyTemplateResponse = null;

  constructor(private router: Router) {}

  storeCurrentSurvey(survey: xSurvey) {
    this.survey$.next(survey);
  }

  storeCurrentTemplate(surveyTemplate: SurveyTemplateResponse) {
    this.surveyTemplate$.next(surveyTemplate);
  }

  /**
   * Return the index at which the first question with the id is founded in the surveyTemplate array
   * @param {string} questionId - The id of the question you are looking for
   */
  getQuestionIndex(questionId: string) {
    return this.surveyTemplate.template.questions.findIndex(
      (question) => question.id === questionId
    );
  }

  /**
   * Return the Cluster where the entity belongs to
   * @param {string} entityId - The ID of the entity for which you need to retrieve its associated cluster.
   * @param {SurveySection[]} clustersCollection - The array of all clusters; It is the surveyForm by default; but it can be the template or a list of subsections
   */
  findEntityParentRecursively(
    entityId: string,
    clustersCollection: SurveySection[] = this.surveyForm
  ): SurveySection | null {
    for (const cat of clustersCollection) {
      if (cat.id === entityId) {
        return cat;
      }

      if (cat.children) {
        const found = this.findEntityParentRecursively(entityId, cat.children);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  /**
   * Using the Nesting Levels, it returns the Exact cluster where the entity belongs to; the result is null if the entity is a Section
   * @param {SurveySection | SurveyQuestion} entity - The whole entity obj for which you need to retrieve its associated cluster.
   * @param {SurveyQuestion} question - The question is explicited when the entity we are passing is the target entity of a multiplication; The question is the question that multiply
   */
  findTheExactParent(
    entity: SurveySection | SurveyQuestion,
    question: SurveySection | SurveyQuestion = null
  ) {
    let rightParent = null;
    question = question ? question : entity;

    const parentOfEntity = this.findEntityParentRecursively(entity.parentSection.id);

    if (parentOfEntity) {
      const parentOfQuestion = this.findEntityParentRecursively(
        question.parentSection ? question.parentSection.id : null
      );
      const indexOfParentQuestion =
        parentOfQuestion.type === surveyItemTypes.SECTION
          ? 0
          : parentOfQuestion.type === surveyItemTypes.SUBSECTION
            ? 1
            : parentOfQuestion.type === surveyItemTypes.GROUP
              ? 2
              : 3;
      const numOfParentQuestionOccurence = parseInt(
        question.nestingLevels.split('.')[indexOfParentQuestion]
      );

      const allPotentialParents = this.getAllParentsOfACluster(parentOfEntity.id);
      allPotentialParents.forEach((parentOfEntity) => {
        const nestingLevelPotentialParent = parentOfEntity.nestingLevels.split('.');
        if (
          parseInt(nestingLevelPotentialParent[indexOfParentQuestion]) ==
          numOfParentQuestionOccurence
        ) {
          rightParent = parentOfEntity;
        }
      });
      return rightParent;
    }
    return rightParent;
  }

  /**
   * Return all the Clusters that match the given clusterId
   * @param {string} clusterId - The ID of the cluster for which you need to retrieve all its duplicated copies.
   * @param survey - The array of all clusters; It is the surveyForm by default; but it can be the template or a list of subsections
   */
  findAllClustersRecursively(
    clusterId: string,
    survey: SurveySection[] = this.surveyForm
  ): SurveySection[] {
    let result: SurveySection[] = [];

    for (const section of survey) {
      if (section.id === clusterId) {
        result.push(section);
      }

      if (section.children) {
        result = result.concat(this.findAllClustersRecursively(clusterId, section.children));
      }
    }

    return result;
  }

  /**
   * Return all Clusters in the given collection
   * @param {SurveySection[]} clustersCollection - The array of all clusters; It is the surveyForm by default; but it can be the template or a list of subsections
   */
  getAllClusters(clustersCollection: SurveySection[] = this.surveyForm) {
    let result: SurveySection[] = [];
    clustersCollection.forEach((cluster) => {
      result.push(cluster);
      if (cluster.children) {
        result = result.concat(this.getAllClusters(cluster.children));
      } else {
        return;
      }
    });
    return result;
  }

  /**
   * Returns all the Clusters in a collection
   * @param {SurveySection[] | SurveySection} clustersCollection - The array of all clusters; It is the surveyForm by default; but it can be the template or a list of subsections
   * @param {boolean} onlyShownAndNotReading - Optional param used to exclude the sections that are NOT shown and are only for reading purposes; By the default, it is false
   */
  getAllClustersRecursively(
    clustersCollection: SurveySection[] | SurveySection,
    onlyShownAndNotReading: boolean = false
  ) {
    let result: SurveySection[] = [];

    if (clustersCollection instanceof Array) {
      clustersCollection.forEach((section) => {
        if (onlyShownAndNotReading && (!section.show || section.isReading)) {
          return;
        }

        result.push(section);

        if (section.children) {
          result = result.concat(
            this.getAllClustersRecursively(section.children, onlyShownAndNotReading)
          );
        }
      });
    } else {
      if (onlyShownAndNotReading && (!clustersCollection.show || clustersCollection.isReading)) {
        return [];
      }

      result.push(clustersCollection);

      if (clustersCollection.children) {
        result = result.concat(
          this.getAllClustersRecursively(clustersCollection.children, onlyShownAndNotReading)
        );
      }
    }

    return result;
  }

  /**
   * Return all upstream Parents of a cluster
   * @param {string} clusterId - The ID of the cluster for which you need to retrieve all its upstream Parents.
   * @param {boolean} excludeHidden - Optional param used to exclude the parents that are NOT shown; By the default, it is true
   * @param {SurveySection[]} clustersCollection - The array of all clusters; It is the surveyForm by default; but it can be the template or a list of subsections
   */
  getAllParentsOfACluster(
    clusterId: string,
    excludeHidden: boolean = true,
    clustersCollection: SurveySection[] = this.surveyForm
  ): SurveySection[] {
    let result: SurveySection[] = [];
    clustersCollection.forEach((section) => {
      if (section.id === clusterId) {
        if (excludeHidden && section.show) {
          result.push(section);
        } else {
          result.push(section);
        }
      }
      if (section.children && section.show) {
        result = result.concat(
          this.getAllParentsOfACluster(clusterId, excludeHidden, section.children)
        );
      }
    });
    return result;
  }

  /**
   * Return all Questions recursively starting from a given questionId
   * @param {string} questionId - The ID of the question for which you want to retrive its object and their copies'ones
   */
  findQuestions(questionId) {
    const result: SurveyQuestion[] = [];
    this.surveyForm.forEach((section) => {
      section.questions.forEach((question) => {
        if (question.id === questionId) {
          result.push(question);
        }
      });
      if (section.children) {
        section.children.forEach((child) => {
          const childQuestions = this.getChildrenQuestions(child);
          childQuestions.forEach((question) => {
            if (question.id === questionId) {
              result.push(question);
            }
          });
        });
      }
    });
    return result;
  }

  /**
   * Return all the Questions from the surveyForm
   * @param {boolean} excludeHidden - optional params used to specified whether you want to retrieve only the question that are shown; By default, it is false
   * @param {SurveySection[]} clustersCollection - The array of all clusters; It is the surveyForm by default; but it can be the template or a list of subsections
   */
  getAllQuestions(
    excludeHidden: boolean = false,
    clustersCollection: SurveySection[] = this.surveyForm
  ) {
    let result: SurveyQuestion[] = [];
    clustersCollection.forEach((section) => {
      if (excludeHidden && !section.show) return;
      section.questions.forEach((question) => {
        result.push(question);
      });
      if (section.children) {
        section.children.forEach((child) => {
          if (excludeHidden && !child.show) return;
          result = result.concat(this.getChildrenQuestions(child, excludeHidden));
        });
      }
    });
    return result;
  }

  /**
   * Return all the downstream Questions belonging to a given cluster
   * @param {SurveySection} cluster - the cluster for which you want to retrieve all its questions
   * @param {boolean} excludeHidden - optional params used to specified whether you want to retrieve only the question that are shown; By default, it is false
   */
  getChildrenQuestions(cluster: SurveySection, excludeHidden: boolean = false) {
    let result: SurveyQuestion[] = [];
    cluster.questions.forEach((question) => {
      if (excludeHidden && !question.show) return;
      result.push(question);
    });
    if (cluster.children) {
      cluster.children.forEach((subCat) => {
        if (excludeHidden && !subCat.show) return;
        result = result.concat(this.getChildrenQuestions(subCat, excludeHidden));
      });
    }
    return result;
  }

  /**
   * Return all the downstream Clusters belonging to a given cluster
   * @param {SurveySection} cluster - the cluster for which you want to retrieve all its questions
   */
  getChildrenClusters(cluster: SurveySection) {
    let children: SurveySection[] = [];
    cluster.children.forEach((child) => {
      children.push(child);
    });
    if (cluster.children) {
      cluster.children.forEach((child) => {
        children = children.concat(this.getChildrenClusters(child));
      });
    }
    return children;
  }

  /**
   * Find the all the downstream Clusters, belonging to a given cluster, and return them after updating their nesting level
   * @param {SurveySection} cluster - the cluster for which you want to retrieve all its questions
   * @param {string} multiplierIndex - optional param that specifies the copy instance of the passed cluster (e.g. 2 if you are passing Manze (2))
   * @param {string} parentType - optional param that specify the cluster type of the cluster parent
   */
  getChildrenClustersAfterUpdatingLevels(
    cluster: SurveySection,
    multiplierIndex?: string,
    parentType?: string
  ) {
    let children: SurveyQuestion[] = [];
    if (cluster.children) {
      cluster.children.forEach((child) => {
        const childCurrentLevels = child.nestingLevels.split('.');
        const parentIndex = parentType === surveyItemTypes.SECTION ? 0 : 1;
        childCurrentLevels[parentIndex] = multiplierIndex
          ? multiplierIndex
          : childCurrentLevels[parentIndex];
        child.nestingLevels = childCurrentLevels.join('.');
        children = children.concat(
          this.getChildrenClustersAfterUpdatingLevels(
            child,
            childCurrentLevels[parentIndex],
            parentType
          )
        );
      });
    }
    // aggiungere il giusto nesting levels to the group
    if (cluster.type == surveyItemTypes.GROUP) {
      const sectionCurrentLevels = cluster.nestingLevels.split('.');
      const parentIndex = parentType === surveyItemTypes.SECTION ? 0 : 1;
      sectionCurrentLevels[parentIndex] = multiplierIndex
        ? multiplierIndex
        : sectionCurrentLevels[parentIndex];
      cluster.nestingLevels = sectionCurrentLevels.join('.');
    }
    return children;
  }

  /**
   * Among a list of Clusters, return all the Clusters already multiplied by the given question
   * @param {SurveySection[]} clusters - the list of clusters among you want to find only the one multiplied by the given question
   * @param {SurveyQuestion} question - the question that has already multiplied some of the clusters in the list
   */
  getMultipliedClustersFromCurrentQuestion(clusters: SurveySection[], question: SurveyQuestion) {
    const alreadyMultiplicatedSections: SurveySection[] = [];
    const parentOfQuestion = this.findEntityParentRecursively(
      question.parentSection ? question.parentSection.id : null
    );
    const indexOfDuplicatedSection =
      parentOfQuestion.type === surveyItemTypes.SECTION
        ? 0
        : parentOfQuestion.type === surveyItemTypes.SUBSECTION
          ? 1
          : parentOfQuestion.type === surveyItemTypes.GROUP
            ? 2
            : 3;
    const numDuplicatedSection = question.nestingLevels.split('.')[indexOfDuplicatedSection];

    clusters.forEach((section) => {
      if (section.rulesParentQuestion.multiplicationIndex == numDuplicatedSection)
        alreadyMultiplicatedSections.push(section);
    });

    return alreadyMultiplicatedSections;
  }

  /**
   * Return the index that must be given to the new Cluster and its children
   * @param {SurveyQuestion} question - The question that triggers the multiplication
   * @param {SurveySection[]} multipliedClusters - The entities that have been already multiplied by the current logic
   * @param {number} entityIndex - The specific segment of the entity (e.g. a section is the first segment (0), a subsection is the second segment (1), group (2), question (3)
   * @param {number} multiplierIndex - The index of the existing clusters + the number of the clusters being added by the loop cycle
   * @param {SurveySection} parent - The parent cluster of the one entity being multiplied
   */
  applyRightNestingLevels(
    question: SurveyQuestion,
    multipliedClusters: SurveySection[],
    entityIndex: number,
    multiplierIndex: number,
    parent: SurveySection
  ) {
    let indexDuplication: number = 0;
    if (question.generalType === 'open') {
      indexDuplication = multiplierIndex;
    }
    if (question.generalType === 'closed') {
      const allExistingIndex = [];
      if (parent) {
        const childrensOfRightParent = this.getChildrenClusters(parent);

        const commonObjects = multipliedClusters.filter((cluster) => {
          return childrensOfRightParent.some(
            (child) => child.id === cluster.id && child.localId === cluster.localId
          );
        });
        // Only duplicated clusters in the right parent
        multipliedClusters = commonObjects;
      }
      if (multipliedClusters.length) {
        for (const cluster of multipliedClusters) {
          // Parse the nestingLevels value to an integer
          const nestingLevels = cluster.nestingLevels.split('.');
          const indexOfEntity = nestingLevels[entityIndex];
          allExistingIndex.push(parseInt(indexOfEntity));
        }
        allExistingIndex.sort((a, b) => a - b);
        for (let i = 0; i < multipliedClusters.length; i++) {
          if (!allExistingIndex.includes(i + 1) && !indexDuplication) {
            indexDuplication = i + 1;
          }
        }
        if (!indexDuplication) {
          indexDuplication = multipliedClusters.length + 1;
        }
      } else {
        indexDuplication = multiplierIndex - multipliedClusters.length;
      }
    }
    return indexDuplication.toString();
  }

  /**
   * Return the module from which the calls is done
   */
  getCurrentModule() {
    let module = '';
    if (this.router.url.includes('/auth/audit')) {
      module = surveyModuleType.AUDIT;
    }
    if (this.router.url.includes('/auth/survey')) {
      module = surveyModuleType.SURVEY;
    }
    return module;
  }
}
